import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const WS_URL = (() => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/^http/, "ws") + "/api/ws/messages";
})();

export default function Messages() {
  const { uid } = useParams();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [other, setOther] = useState(null);
  const [otherTyping, setOtherTyping] = useState(false);
  const [presence, setPresence] = useState({});  // userId -> bool
  const wsRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTypingSent = useRef(0);
  const msgEndRef = useRef(null);
  const nav = useNavigate();

  // ---- WebSocket lifecycle ----
  useEffect(() => {
    const token = localStorage.getItem("cauldron_token");
    if (!token) return;
    let ws;
    let closed = false;
    let reconnectTimer = null;

    const connect = () => {
      ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => {};
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message") {
            const m = data.message;
            setMsgs((prev) => {
              if (prev.some((x) => x.id === m.id)) return prev;
              return [...prev, m];
            });
            setThreads((prev) => {
              const otherId = m.fromUserId === user?.id ? m.toUserId : m.fromUserId;
              const idx = prev.findIndex((t) => t.otherUser.id === otherId);
              if (idx === -1) {
                // New thread; reload threads list
                loadThreads();
                return prev;
              }
              const next = [...prev];
              next[idx] = { ...next[idx], lastMessage: m, unread: (m.toUserId === user?.id && otherId !== uid) ? (next[idx].unread || 0) + 1 : next[idx].unread };
              return next;
            });
          } else if (data.type === "typing") {
            if (data.fromUserId && data.fromUserId === uid) {
              setOtherTyping(true);
              clearTimeout(typingTimer.current);
              typingTimer.current = setTimeout(() => setOtherTyping(false), 3000);
            }
          } else if (data.type === "read") {
            setMsgs((prev) => prev.map((m) => (m.fromUserId === user?.id && m.toUserId === data.fromUserId ? { ...m, read: true } : m)));
          } else if (data.type === "presence") {
            setPresence((p) => ({ ...p, [data.userId]: data.online }));
          }
        } catch {}
      };
      ws.onclose = () => {
        if (!closed) reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => {};
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (ws && ws.readyState <= 1) ws.close();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, uid]);

  const loadThreads = async () => {
    const { data } = await api.get("/messages/threads");
    setThreads(data);
    const map = {};
    data.forEach((t) => { if (t.online !== undefined) map[t.otherUser.id] = t.online; });
    setPresence((p) => ({ ...map, ...p }));
  };
  const loadThread = async (id) => {
    const { data } = await api.get(`/messages/${id}`); setMsgs(data);
    const t = threads.find((tt) => tt.otherUser.id === id);
    if (t) setOther(t.otherUser);
    else { try { const u = await api.get(`/providers/${id}`); setOther(u.data.user); } catch {} }
    // Mark read on open
    try { await api.post(`/messages/${id}/read`, {}); } catch {}
    setThreads((prev) => prev.map((t) => t.otherUser.id === id ? { ...t, unread: 0 } : t));
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (uid) loadThread(uid); /* eslint-disable-next-line */ }, [uid, threads.length]);
  useEffect(() => {
    if (msgEndRef.current) msgEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSent.current < 1500) return;
    lastTypingSent.current = now;
    if (wsRef.current?.readyState === 1 && uid) {
      wsRef.current.send(JSON.stringify({ type: "typing", toUserId: uid }));
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !uid) return;
    const content = text;
    setText("");
    try {
      await api.post("/messages", { toUserId: uid, content });
      // The WS broadcast will populate msgs — no need to setMsgs here.
    } catch {
      setText(content);
    }
  };

  const isOnline = (userId) => !!presence[userId] || threads.find((t) => t.otherUser.id === userId)?.online;

  return (
    <Layout>
      <div className="parchment grid grid-cols-1 md:grid-cols-3 min-h-[600px]">
        <aside className="border-r border-secondary/15 p-4 max-h-[640px] overflow-y-auto scroll-thin" data-testid="messages-thread-list">
          <div className="label-mystic mb-3">Conversations</div>
          {threads.map((t) => (
            <button key={t.otherUser.id} onClick={() => nav(`/messages/${t.otherUser.id}`)}
              className={`w-full text-left p-3 rounded-sm hover:bg-secondary/5 ${uid===t.otherUser.id?"bg-secondary/10":""}`}
              data-testid={`thread-${t.otherUser.id}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isOnline(t.otherUser.id) ? "bg-emerald-400" : "bg-foreground/20"}`} data-testid={`presence-${t.otherUser.id}`} />
                <span className="font-serif">{t.otherUser.name}</span>
                {t.unread > 0 && <span className="ml-auto text-xs px-2 py-0.5 bg-primary/30 text-primary rounded-sm" data-testid={`unread-${t.otherUser.id}`}>{t.unread}</span>}
              </div>
              <div className="text-xs text-foreground/60 truncate mt-1">{t.lastMessage.content}</div>
            </button>
          ))}
          {threads.length === 0 && <div className="text-foreground/50 text-sm italic mt-6">No conversations yet.</div>}
        </aside>
        <section className="md:col-span-2 flex flex-col">
          {uid ? (
            <>
              <div className="border-b border-secondary/15 p-4 flex items-center gap-3">
                <div>
                  <div className="font-serif text-xl flex items-center gap-2" data-testid="thread-header">
                    <span className={`w-2 h-2 rounded-full ${isOnline(uid) ? "bg-emerald-400" : "bg-foreground/20"}`} />
                    {other?.name || "Conversation"}
                  </div>
                  <div className="text-xs text-foreground/50">{isOnline(uid) ? "Online" : "Offline"}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-thin max-h-[440px]" data-testid="messages-list">
                {msgs.map((m) => {
                  const mine = m.fromUserId === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine?"justify-end":"justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-sm ${mine?"bg-primary/20 border border-primary/30":"bg-secondary/10 border border-secondary/20"}`} data-testid={`message-${m.id}`}>
                        <div className="text-sm text-foreground/90">{m.content}</div>
                        <div className="label-mystic mt-1 text-[10px] flex items-center gap-1.5">
                          {new Date(m.createdAt).toLocaleTimeString()}
                          {mine && (m.read ? <span className="text-secondary">· read</span> : <span className="text-foreground/40">· sent</span>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {otherTyping && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="max-w-[70%] px-4 py-2 rounded-sm bg-secondary/10 border border-secondary/20 italic text-foreground/60 text-sm">
                      {other?.name?.split(" ")[0] || "Typing"}…
                    </div>
                  </div>
                )}
                <div ref={msgEndRef} />
              </div>
              <form onSubmit={send} className="p-4 border-t border-secondary/15 flex gap-2">
                <input value={text} onChange={(e)=>{ setText(e.target.value); sendTyping(); }} placeholder="Speak across the veil..."
                  className="flex-1 bg-input border border-secondary/20 p-3 rounded-sm focus:outline-none focus:border-secondary"
                  data-testid="message-input" />
                <button className="bg-primary text-primary-foreground px-6 rounded-sm uppercase tracking-wider text-xs" data-testid="message-send-btn">Send</button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-foreground/50 font-serif text-2xl">Select a conversation</div>
          )}
        </section>
      </div>
    </Layout>
  );
}
